import { useEffect, useRef, useState } from 'react';
import './BibleProgressCalendar.css';

/**
 * @param {Object} props
 * @param {Set<number>|null} [props.neededChapterIds] chapters still needed for
 *   the community's next completion — highlighted as "still needed" (WS1).
 */
const BibleProgressCalendar = ({ chapters, chapterReadCounts, neededChapterIds = null }) => {
  const [hoveredChapter, setHoveredChapter] = useState(null);
  // Chapters whose count just went up, so a log by anyone visibly lights a cell.
  const [recentlyChanged, setRecentlyChanged] = useState(() => new Set());
  const previousCountsRef = useRef(chapterReadCounts);

  useEffect(() => {
    const previous = previousCountsRef.current;
    previousCountsRef.current = chapterReadCounts;
    if (previous === chapterReadCounts) return undefined;

    const changed = new Set();
    for (const [chapterId, count] of Object.entries(chapterReadCounts || {})) {
      if ((previous?.[chapterId] || 0) < count) changed.add(Number(chapterId));
    }
    if (changed.size === 0) return undefined;

    setRecentlyChanged(changed);
    const timer = setTimeout(() => setRecentlyChanged(new Set()), 2000);
    return () => clearTimeout(timer);
  }, [chapterReadCounts]);

  // Group chapters by book while maintaining biblical order
  const getBookGroups = () => {
    const bookGroups = [];
    let currentBookName = null;
    let currentBookChapters = [];

    chapters.forEach(chapter => {
      if (currentBookName !== chapter.book) {
        if (currentBookName) {
          bookGroups.push({
            book: currentBookName,
            testament: currentBookChapters[0].testament,
            chapters: currentBookChapters
          });
        }
        currentBookName = chapter.book;
        currentBookChapters = [chapter];
      } else {
        currentBookChapters.push(chapter);
      }
    });

    // Add the last book
    if (currentBookName) {
      bookGroups.push({
        book: currentBookName,
        testament: currentBookChapters[0].testament,
        chapters: currentBookChapters
      });
    }

    return bookGroups;
  };

  const getChapterStatus = (chapterId) => {
    const readCount = chapterReadCounts[chapterId] || 0;
    if (readCount === 0) return 'unread';
    if (readCount === 1) return 'read-1';
    if (readCount <= 3) return 'read-2-3';
    if (readCount <= 5) return 'read-4-5';
    if (readCount <= 10) return 'read-6-10';
    if (readCount <= 20) return 'read-11-20';
    if (readCount <= 35) return 'read-21-35';
    if (readCount <= 50) return 'read-36-50';
    return 'read-50-plus';
  };

  const getChapterClassName = (chapter) => {
    const status = getChapterStatus(chapter.id);
    const isHovered = hoveredChapter?.id === chapter.id;
    const isNeeded = neededChapterIds?.has(chapter.id);
    const justChanged = recentlyChanged.has(chapter.id);
    return [
      'progress-chapter-cell',
      status,
      isHovered ? 'hovered' : '',
      isNeeded ? 'needed' : '',
      justChanged ? 'just-read' : ''
    ].filter(Boolean).join(' ');
  };

  const handleChapterHover = (chapter) => {
    setHoveredChapter(chapter);
  };

  const handleChapterLeave = () => {
    setHoveredChapter(null);
  };

  const bookGroups = getBookGroups();
  const oldTestamentBooks = bookGroups.filter(bg => bg.testament === 'Old');
  const newTestamentBooks = bookGroups.filter(bg => bg.testament === 'New');

  const renderBookGroup = (bookGroup) => (
    <div key={bookGroup.book} className="book-group">
      <div className="book-title">
        {bookGroup.book}
      </div>
      <div className="chapters-grid">
        {bookGroup.chapters.map(chapter => (
          <div
            key={chapter.id}
            className={getChapterClassName(chapter)}
            onMouseEnter={() => handleChapterHover(chapter)}
            onMouseLeave={handleChapterLeave}
            title={`${chapter.book} ${chapter.chapter}`}
          >
            {chapter.chapter}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="bible-progress-calendar">
      <div className="calendar-legend">
        {neededChapterIds && neededChapterIds.size > 0 && (
          <div className="legend-item">
            <div className="legend-color needed"></div>
            <span>Still needed</span>
          </div>
        )}
        <div className="legend-item">
          <div className="legend-color unread"></div>
          <span>Unread</span>
        </div>
        <div className="legend-item">
          <div className="legend-color read-1"></div>
          <span>1x</span>
        </div>
        <div className="legend-item">
          <div className="legend-color read-2-3"></div>
          <span>2-3x</span>
        </div>
        <div className="legend-item">
          <div className="legend-color read-4-5"></div>
          <span>4-5x</span>
        </div>
        <div className="legend-item">
          <div className="legend-color read-6-10"></div>
          <span>6-10x</span>
        </div>
        <div className="legend-item">
          <div className="legend-color read-11-20"></div>
          <span>11-20x</span>
        </div>
        <div className="legend-item">
          <div className="legend-color read-21-35"></div>
          <span>21-35x</span>
        </div>
        <div className="legend-item">
          <div className="legend-color read-36-50"></div>
          <span>36-50x</span>
        </div>
        <div className="legend-item">
          <div className="legend-color read-50-plus"></div>
          <span>50+x</span>
        </div>
      </div>

      {/* Hover Tooltip */}
      {hoveredChapter && (
        <div className="chapter-tooltip">
          <strong>{hoveredChapter.book} {hoveredChapter.chapter}</strong>
          <div className="tooltip-stats">
            <span>Read {chapterReadCounts[hoveredChapter.id] || 0} time{(chapterReadCounts[hoveredChapter.id] || 0) !== 1 ? 's' : ''}</span>
            <span>{hoveredChapter.testament} Testament</span>
            <span>{hoveredChapter.verseCount} verses</span>
            {neededChapterIds?.has(hoveredChapter.id) && (
              <span className="tooltip-needed">Still needed for the next completion</span>
            )}
          </div>
        </div>
      )}

      {/* Old Testament */}
      <div className="testament-section">
        <div className="testament-header">
          <h3>Old Testament</h3>
          <span className="testament-progress">
            {oldTestamentBooks.reduce((sum, bg) => 
              sum + bg.chapters.filter(ch => chapterReadCounts[ch.id] > 0).length, 0
            )} / {oldTestamentBooks.reduce((sum, bg) => sum + bg.chapters.length, 0)} chapters
            ({oldTestamentBooks.reduce((sum, bg) => 
              sum + bg.chapters.reduce((chSum, ch) => chSum + (chapterReadCounts[ch.id] || 0), 0), 0
            )} total reads)
          </span>
        </div>
        <div className="books-container">
          {oldTestamentBooks.map(renderBookGroup)}
        </div>
      </div>

      {/* New Testament */}
      <div className="testament-section">
        <div className="testament-header">
          <h3>New Testament</h3>
          <span className="testament-progress">
            {newTestamentBooks.reduce((sum, bg) => 
              sum + bg.chapters.filter(ch => chapterReadCounts[ch.id] > 0).length, 0
            )} / {newTestamentBooks.reduce((sum, bg) => sum + bg.chapters.length, 0)} chapters
            ({newTestamentBooks.reduce((sum, bg) => 
              sum + bg.chapters.reduce((chSum, ch) => chSum + (chapterReadCounts[ch.id] || 0), 0), 0
            )} total reads)
          </span>
        </div>
        <div className="books-container">
          {newTestamentBooks.map(renderBookGroup)}
        </div>
      </div>
    </div>
  );
};

export default BibleProgressCalendar;
